import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Backdrop,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import FileDialog from "components/FileDialog";

import SelectProceso from "./selectProceso";
import ComunicacionesApi from "api/comunicacionesApi";
import ProcesosApi from "api/procesosApi";
import { obtenerMensajeError } from "utils/apiErrors";

const ESTADO_ACUERDO = "ACUERDO DE PAGO";
const NUEVO_JUZGADO_INICIAL = {
  juzgado: "",
  email: "",
  radicado: "",
  demandante: "",
  demandado: "",
};

const esEmailValido = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const InformeEstadoProceso = ({ open, onClose, onSuccess }) => {
  const [proceso, setProceso] = useState(null);
  const [showProcesoModal, setShowProcesoModal] = useState(false);
  const [datos, setDatos] = useState(null);
  const [estado, setEstado] = useState("");
  const [texto, setTexto] = useState("");
  const [fechaActa, setFechaActa] = useState("");
  const [seleccionados, setSeleccionados] = useState({});
  const [mostrarNuevoJuzgado, setMostrarNuevoJuzgado] = useState(false);
  const [nuevoJuzgado, setNuevoJuzgado] = useState(NUEVO_JUZGADO_INICIAL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined);
  const [pdfUrl, setPdfUrl] = useState("");

  const cargarDatos = async (procesoId, idsASeleccionar = null) => {
    try {
      setLoading(true);
      const response = await ComunicacionesApi.obtenerDatosInformeEstado(procesoId);
      setDatos(response.data);
      // Por defecto se seleccionan todos los juzgados con correo
      setSeleccionados((prev) => {
        const nuevos = {};
        response.data.juzgados.forEach((juzgado) => {
          if (!juzgado.email) return;
          nuevos[juzgado.id] = idsASeleccionar
            ? prev[juzgado.id] || idsASeleccionar.includes(juzgado.id)
            : true;
        });
        return nuevos;
      });
      setError(undefined);
    } catch (error) {
      console.error("Error al obtener los datos del Informe de Estado:", error);
      setError(obtenerMensajeError(error, "Error al cargar los datos del proceso."));
    } finally {
      setLoading(false);
    }
  };

  const handleProcesoSelect = (procesoSeleccionado) => {
    setProceso(procesoSeleccionado);
    setShowProcesoModal(false);
    setEstado("");
    setTexto("");
    setFechaActa("");
    setDatos(null);
    cargarDatos(procesoSeleccionado.id);
  };

  const handleEstadoChange = (nuevoEstado) => {
    setEstado(nuevoEstado);
    if (nuevoEstado === ESTADO_ACUERDO) {
      setFechaActa(datos?.fecha_acta_acuerdo || "");
    } else {
      setTexto(datos?.textos_sugeridos?.[nuevoEstado] || "");
    }
  };

  const construirPayload = () => ({
    estado,
    texto: estado === ESTADO_ACUERDO ? "" : texto,
    fecha_acta: estado === ESTADO_ACUERDO ? fechaActa : "",
    juzgados_ids: Object.keys(seleccionados)
      .filter((id) => seleccionados[id])
      .map(Number),
  });

  const validar = () => {
    if (!proceso) return "Debes seleccionar un Proceso.";
    if (!estado) return "Debes seleccionar el Estado del Proceso.";
    if (estado === ESTADO_ACUERDO && !fechaActa)
      return "Debes indicar la fecha del Acta de Acuerdo de Pago.";
    if (estado !== ESTADO_ACUERDO && !texto.trim())
      return "Debes registrar el texto del estado del proceso.";
    if (!Object.values(seleccionados).some(Boolean))
      return "Debes seleccionar al menos un Juzgado.";
    return null;
  };

  const handleAgregarJuzgado = async () => {
    const faltantes = Object.entries(nuevoJuzgado).filter(([, valor]) => !valor.trim());
    if (faltantes.length) {
      setError("Debes diligenciar todos los datos del nuevo proceso judicial.");
      return;
    }
    if (!esEmailValido(nuevoJuzgado.email.trim())) {
      setError("El correo electrónico del juzgado no es válido.");
      return;
    }
    try {
      setLoading(true);
      // Se guarda en el expediente del proceso (Procesos Judiciales) para futuras comunicaciones
      const response = await ProcesosApi.crearProcesoJudicial(proceso.id, {
        juzgado: nuevoJuzgado.juzgado.trim(),
        email: nuevoJuzgado.email.trim(),
        radicado: nuevoJuzgado.radicado.trim(),
        demandante: nuevoJuzgado.demandante.trim(),
        demandado: nuevoJuzgado.demandado.trim(),
      });
      setNuevoJuzgado(NUEVO_JUZGADO_INICIAL);
      setMostrarNuevoJuzgado(false);
      await cargarDatos(proceso.id, [response.data.id]);
    } catch (error) {
      console.error("Error al registrar el proceso judicial:", error);
      setError(obtenerMensajeError(error, "Error al registrar el proceso judicial."));
      setLoading(false);
    }
  };

  const handleVistaPrevia = async () => {
    const mensaje = validar();
    if (mensaje) {
      setError(mensaje);
      return;
    }
    try {
      setLoading(true);
      const response = await ComunicacionesApi.previsualizarInformeEstado(
        proceso.id,
        construirPayload()
      );
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      setPdfUrl(url);
      setError(undefined);
    } catch (error) {
      console.error("Error al generar la vista previa:", error);
      setError(obtenerMensajeError(error, "Error al generar la vista previa del memorial."));
    } finally {
      setLoading(false);
    }
  };

  const handleCerrarVistaPrevia = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl("");
  };

  const resetState = () => {
    setProceso(null);
    setDatos(null);
    setEstado("");
    setTexto("");
    setFechaActa("");
    setSeleccionados({});
    setMostrarNuevoJuzgado(false);
    setNuevoJuzgado(NUEVO_JUZGADO_INICIAL);
    setError(undefined);
  };

  const handleSubmit = async () => {
    const mensaje = validar();
    if (mensaje) {
      setError(mensaje);
      return;
    }
    const confirmacion = window.confirm(
      "¿Está seguro(a) de crear el Informe de Estado? Luego podrá enviarlo a los juzgados con Notificar Comunicación."
    );
    if (!confirmacion) return;
    try {
      setLoading(true);
      const response = await ComunicacionesApi.registrarInformeEstado(
        proceso.id,
        construirPayload()
      );
      resetState();
      onSuccess(response.data);
    } catch (error) {
      console.error("Error al crear el Informe de Estado:", error);
      setError(obtenerMensajeError(error, "Error al crear el Informe de Estado."));
    } finally {
      setLoading(false);
    }
  };

  const hCClose = () => {
    resetState();
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={hCClose} maxWidth="md" fullWidth>
        <DialogTitle>Informe de Estado del Proceso (Memorial a Juzgados)</DialogTitle>
        <DialogContent>
          <Backdrop
            open={loading}
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              <SuiTypography variant="h6" fontWeight="light">
                Proceso
              </SuiTypography>
              <SuiButton
                variant="outlined"
                buttonColor="dark"
                fullWidth
                onClick={() => setShowProcesoModal(true)}
              >
                {proceso
                  ? `${proceso.cliente_nombre} - ${proceso.identificador}`
                  : "Seleccionar Proceso"}
              </SuiButton>
            </Grid>

            {datos && (
              <>
                <Grid size={6}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Estado del Proceso
                  </SuiTypography>
                  <Select
                    value={estado}
                    onChange={(e) => handleEstadoChange(e.target.value)}
                    fullWidth
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Seleccione un Estado</em>
                    </MenuItem>
                    {datos.estados.map((opcion) => (
                      <MenuItem key={opcion} value={opcion}>
                        {opcion}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                {estado === ESTADO_ACUERDO && (
                  <Grid size={6}>
                    <SuiTypography variant="h6" fontWeight="light">
                      Fecha del Acta de Acuerdo de Pago
                    </SuiTypography>
                    <SuiInput
                      type="date"
                      value={fechaActa}
                      onChange={(e) => setFechaActa(e.target.value)}
                      fullWidth
                    />
                    {!datos.fecha_acta_acuerdo && (
                      <SuiTypography variant="caption" color="warning">
                        El proceso no tiene una audiencia con resultado ACUERDO DE PAGO; indique la
                        fecha del acta.
                      </SuiTypography>
                    )}
                  </Grid>
                )}
                {estado && estado !== ESTADO_ACUERDO && (
                  <Grid size={12}>
                    <SuiTypography variant="h6" fontWeight="light">
                      Texto del estado (revíselo antes de enviar; cada línea es un párrafo)
                    </SuiTypography>
                    <SuiInput
                      value={texto}
                      onChange={(e) => setTexto(e.target.value)}
                      multiline
                      rows={6}
                      fullWidth
                    />
                  </Grid>
                )}

                <Grid size={12}>
                  <SuiBox display="flex" justifyContent="space-between" alignItems="center">
                    <SuiTypography variant="h6" fontWeight="light">
                      Juzgados destinatarios
                    </SuiTypography>
                    <SuiButton
                      size="small"
                      variant="outlined"
                      buttonColor="info"
                      onClick={() => setMostrarNuevoJuzgado((prev) => !prev)}
                    >
                      {mostrarNuevoJuzgado ? "Cancelar" : "Agregar Proceso Judicial"}
                    </SuiButton>
                  </SuiBox>
                  {datos.juzgados.length === 0 && (
                    <SuiTypography variant="caption" color="text">
                      El proceso no tiene procesos judiciales registrados.
                    </SuiTypography>
                  )}
                  {datos.juzgados.map((juzgado) => (
                    <SuiBox key={juzgado.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(seleccionados[juzgado.id])}
                            disabled={!juzgado.email}
                            onChange={(e) =>
                              setSeleccionados((prev) => ({
                                ...prev,
                                [juzgado.id]: e.target.checked,
                              }))
                            }
                          />
                        }
                        label={
                          <SuiTypography variant="caption">
                            <strong>{juzgado.juzgado}</strong> - Rad. {juzgado.radicado} -{" "}
                            {juzgado.email || (
                              <span style={{ color: "red" }}>Sin correo electrónico</span>
                            )}
                          </SuiTypography>
                        }
                      />
                    </SuiBox>
                  ))}
                </Grid>

                {mostrarNuevoJuzgado && (
                  <Grid size={12}>
                    <SuiBox p={2} sx={{ border: "1px solid #ddd", borderRadius: "8px" }}>
                      <SuiTypography variant="caption" color="text">
                        El proceso judicial se guardará en el expediente del proceso.
                      </SuiTypography>
                      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                        {[
                          ["juzgado", "Juzgado"],
                          ["email", "Correo electrónico"],
                          ["radicado", "Radicado"],
                          ["demandante", "Demandante"],
                          ["demandado", "Demandado"],
                        ].map(([campo, etiqueta]) => (
                          <Grid size={campo === "juzgado" ? 12 : 6} key={campo}>
                            <SuiTypography variant="h6" fontWeight="light">
                              {etiqueta}
                            </SuiTypography>
                            <SuiInput
                              value={nuevoJuzgado[campo]}
                              onChange={(e) =>
                                setNuevoJuzgado((prev) => ({ ...prev, [campo]: e.target.value }))
                              }
                              fullWidth
                            />
                          </Grid>
                        ))}
                        <Grid size={12}>
                          <SuiButton
                            variant="gradient"
                            buttonColor="info"
                            size="small"
                            onClick={handleAgregarJuzgado}
                          >
                            Guardar Proceso Judicial
                          </SuiButton>
                        </Grid>
                      </Grid>
                    </SuiBox>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <SuiButton onClick={hCClose} buttonColor="light">
            Cancelar
          </SuiButton>
          <SuiButton
            variant="outlined"
            buttonColor="dark"
            onClick={handleVistaPrevia}
            disabled={loading || !datos}
          >
            Vista Previa
          </SuiButton>
          <SuiButton
            variant="gradient"
            buttonColor="dark"
            onClick={handleSubmit}
            disabled={loading || !datos}
          >
            Crear Informe
          </SuiButton>
        </DialogActions>
      </Dialog>

      <SelectProceso
        open={showProcesoModal}
        onClose={() => setShowProcesoModal(false)}
        onSelect={handleProcesoSelect}
      />

      <FileDialog
        open={Boolean(pdfUrl)}
        onClose={handleCerrarVistaPrevia}
        title="Vista previa - Informe de Estado (primer juzgado seleccionado)"
        content={
          <iframe
            src={pdfUrl}
            width="100%"
            height="500px"
            style={{ border: "none" }}
            key={pdfUrl}
            title="Vista previa Informe de Estado"
          />
        }
      />
    </>
  );
};

InformeEstadoProceso.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default InformeEstadoProceso;
